import { Trans, useLingui } from "@lingui/react/macro";
import { Button } from "@orbin/ui-web";
import { useEffect, useRef, useState } from "react";
import { depletedFromError, sharingDeniedFromError } from "../lib/metering";
import { rpc } from "../lib/rpc";
import type { StarterKind } from "../lib/starter-tasks";
import { starterTasks } from "../lib/starter-tasks";
import { errorText } from "../lib/user-error";
import { AiDataSharingPrompt } from "./AiDataSharingPrompt";
import { BrandMoment } from "./BrandMoment";
import { TrialStart } from "./billing/TrialStart";
import { RechargePrompt } from "./RechargePrompt";

/** The first sign-in with no teammates: one action that shows an agent working on its own computer. */
export function FirstRun({
  computerAvailable,
  onStart,
  onNewBot,
}: {
  computerAvailable: boolean;
  onStart: (kind: StarterKind) => Promise<void>;
  onNewBot: () => void;
}) {
  const { t } = useLingui();
  const [busy, setBusy] = useState<StarterKind | null>(null);
  const starting = useRef(false);
  // The starter the customer picked, so an allowed AI data sharing sends the same task again.
  const lastKind = useRef<StarterKind | null>(null);
  const [failure, setFailure] = useState<unknown>(null);
  // A new account on a billing deployment starts its 7-day trial before the first task.
  const [needsTrial, setNeedsTrial] = useState(false);
  useEffect(() => {
    rpc.billing
      .overview()
      .then((overview) => setNeedsTrial(overview.state === "free" && overview.trialAvailable))
      .catch(() => undefined);
  }, []);
  const tasks = starterTasks();
  const [main, ...others] = tasks;

  async function start(kind: StarterKind) {
    if (starting.current) return;
    starting.current = true;
    lastKind.current = kind;
    setBusy(kind);
    setFailure(null);
    try {
      await onStart(kind);
    } catch (cause) {
      setFailure(cause);
      starting.current = false;
      setBusy(null);
    }
  }

  const depleted = depletedFromError(failure);
  const sharing = sharingDeniedFromError(failure);
  return (
    <div data-testid="first-run" className="grid flex-1 place-items-center px-6">
      <div className="flex w-full max-w-sm flex-col items-center gap-5 text-center">
        <BrandMoment kind="welcome" size={144} />
        <h1 className="font-display text-[44px] font-bold leading-none tracking-tight text-foreground">
          {t`Leave it to Preá.`}
        </h1>
        {needsTrial ? (
          <div className="flex w-full flex-col gap-3 text-start">
            <p className="text-center text-sm text-muted-foreground">{t`7 days free, no card.`}</p>
            <TrialStart
              onStarted={() => setNeedsTrial(false)}
              onSkip={() => setNeedsTrial(false)}
            />
          </div>
        ) : (
          <>
            <Button
              size="lg"
              className="w-full"
              disabled={busy !== null}
              data-testid="first-run-start"
              onClick={() => void start("watch")}
            >
              {main?.label}
            </Button>
            {!computerAvailable ? (
              <p className="text-sm text-muted-foreground">
                <Trans>Computers are unavailable here.</Trans>
              </p>
            ) : null}
            <div className="flex w-full flex-wrap justify-center gap-2">
              {others.map((task) => (
                <Button
                  key={task.kind}
                  variant="outline"
                  className="h-auto min-h-9 rounded-full px-4 py-1.5 font-normal whitespace-normal"
                  disabled={busy !== null}
                  data-testid={`first-run-${task.kind}`}
                  onClick={() => void start(task.kind)}
                >
                  {task.label}
                </Button>
              ))}
              <Button
                variant="outline"
                className="h-auto min-h-9 rounded-full px-4 py-1.5 font-normal"
                onClick={onNewBot}
              >
                {t`New teammate`}
              </Button>
            </div>
          </>
        )}
        {failure ? (
          <div role="alert" className="text-sm text-destructive">
            {depleted ? (
              <RechargePrompt depleted={depleted} />
            ) : sharing ? (
              <AiDataSharingPrompt
                recipients={sharing}
                onAllowed={() => {
                  const kind = lastKind.current;
                  if (kind) void start(kind);
                }}
              />
            ) : (
              errorText(failure)
            )}
          </div>
        ) : null}
      </div>
    </div>
  );
}
